import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Calendar, GitCommit, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { fetchGitHubActivity, VERIFIED_FALLBACK_STATS } from "../../services/github";

const LEVELS = [
  "bg-white/[0.04]",         // Level 0 (no activity)
  "bg-emerald-900/60",       // Level 1 (1-2)
  "bg-emerald-600/75",       // Level 2 (3-6)
  "bg-emerald-500/90",       // Level 3 (7-12)
  "bg-emerald-400",          // Level 4 (13+)
];

export default function GithubGrid() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchGitHubActivity().then((res) => {
      if (isMounted) {
        setData(res);
        setLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const total = data?.totalContributionsLastYear ?? VERIFIED_FALLBACK_STATS.totalContributionsLastYear;
  const timeRange = data?.timeRange ?? VERIFIED_FALLBACK_STATS.timeRange;
  const monthly = data?.monthlyCadence || [];
  const maxMonthly = monthly.length > 0 ? Math.max(...monthly.map((m) => m.count), 1) : 1;

  return (
    <div className="space-y-6">
      
      {/* 1. CONTRIBUTION HEATMAP */}
      <div className="rounded-xl border border-border/70 bg-card/50 backdrop-blur-sm p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary font-medium">
                Public Activity Heatmap
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">·</span>
              <span className="font-mono text-[10px] text-foreground/80">@Maverick-list</span>
            </div>
            <p className="font-mono text-[10.5px] text-muted-foreground mt-0.5">
              Source: GitHub Public Activity API · Rolling 365 Days ({timeRange})
            </p>
          </div>

          <div className="flex items-center gap-3">
            {data?.isCached && (
              <span className="inline-flex items-center gap-1 font-mono text-[9.5px] tracking-wider uppercase text-muted-foreground/80 px-2 py-0.5 rounded border border-border/60 bg-secondary/30">
                <RefreshCw className="h-2.5 w-2.5" /> 1h Cache
              </span>
            )}
            <a
              href="https://github.com/Maverick-list"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.18em] uppercase text-primary hover:underline"
            >
              GitHub Profile <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>

        {/* Heatmap Grid */}
        {loading ? (
          <div className="h-32 flex items-center justify-center font-mono text-[12px] text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin mr-2 text-primary" />
            Loading verified GitHub calendar...
          </div>
        ) : (
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-[3px] min-w-[720px]" role="img" aria-label={`GitHub contribution calendar: ${total} contributions in the last year`}>
              {data?.gridColumns ? (
                data.gridColumns.map((col, colIdx) => (
                  <div key={colIdx} className="flex flex-col gap-[3px]">
                    {col.map((day, rowIdx) => {
                      const levelIndex = Math.min(Math.max(day.level || 0, 0), 4);
                      return (
                        <div
                          key={day.date || `${colIdx}-${rowIdx}`}
                          title={`${day.date}: ${day.count} contribution${day.count === 1 ? "" : "s"}`}
                          className={`h-[11px] w-[11px] rounded-[2px] transition-colors ${LEVELS[levelIndex]} hover:ring-1 hover:ring-primary`}
                        />
                      );
                    })}
                  </div>
                ))
              ) : (
                <div className="py-8 text-center w-full font-mono text-[12px] text-muted-foreground">
                  Snapshot active · Visit github.com/Maverick-list for live activity.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Heatmap Footer Legend & Metrics */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-5 pt-4 border-t border-border/50 text-[12px]">
          <div className="flex items-center gap-2">
            <GitCommit className="h-3.5 w-3.5 text-primary" />
            <span className="font-mono text-foreground font-medium">{total}</span>
            <span className="text-muted-foreground">verified contributions in the last 12 months</span>
          </div>

          <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
            <span>Less</span>
            {LEVELS.map((c, i) => (
              <span key={i} className={`h-[10px] w-[10px] rounded-[2px] border border-border/40 ${c}`} />
            ))}
            <span>More</span>
          </div>
        </div>
      </div>

      {/* 2. MONTHLY CADENCE BREAKDOWN */}
      <div className="rounded-xl border border-border/70 bg-card/50 backdrop-blur-sm p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary font-medium">
              Monthly Cadence Breakdown
            </span>
            <p className="font-mono text-[10.5px] text-muted-foreground mt-0.5">
              Aggregated from verified daily commit & contribution events (UTC)
            </p>
          </div>
          <span className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground/80 px-2.5 py-1 rounded-full border border-border/60 bg-secondary/30">
            Metric: Public Calendar Events
          </span>
        </div>

        {monthly.length > 0 ? (
          <div>
            {/* Bars Container */}
            <div className="flex items-end justify-between gap-2 sm:gap-3 h-44 px-2 pt-4">
              {monthly.map((m, idx) => {
                const heightPercent = maxMonthly > 0 ? Math.max((m.count / maxMonthly) * 100, 4) : 4;
                return (
                  <div key={m.monthKey} className="flex-1 flex flex-col items-center justify-end h-full group">
                    {/* Hover Tooltip Value */}
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] text-primary mb-1 font-medium">
                      {m.count}
                    </span>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${heightPercent}%` }}
                      transition={{ duration: 0.6, delay: idx * 0.04 }}
                      className={`w-full max-w-[32px] rounded-t transition-all ${
                        m.count > 0
                          ? "bg-gradient-to-t from-violet-600/80 to-violet-400 group-hover:brightness-125 shadow-[0_0_15px_rgba(139,92,246,0.25)]"
                          : "bg-white/[0.04]"
                      }`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Month Labels */}
            <div className="flex justify-between mt-3 px-2 border-t border-border/40 pt-2">
              {monthly.map((m) => (
                <span
                  key={m.monthKey}
                  className="flex-1 text-center font-mono text-[10px] text-muted-foreground/80"
                >
                  {m.label}
                </span>
              ))}
            </div>
          </div>
        ) : (
          <div className="py-12 text-center font-mono text-[12px] text-muted-foreground">
            Monthly cadence summary actively updating from public calendar.
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-border/40 flex flex-wrap items-center justify-between text-[11px] font-mono text-muted-foreground gap-2">
          <span>Public Repositories: {data?.publicRepos ?? 38}</span>
          <span>Verified against GitHub REST / Calendar API</span>
        </div>
      </div>

    </div>
  );
}
